import { getBuildVersion } from './buildInfo.js';

const RELEASE_CHECK_INTERVAL_MS = 60_000;
const RELEASE_ATTEMPT_STORAGE_KEY = 'nettgrav:frontend-release-attempt';
let pendingReleaseId = null;
let releaseCheckFlight = null;

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const readAttemptedReleaseId = () => {
  try {
    return toStringOrNull(window.sessionStorage?.getItem(RELEASE_ATTEMPT_STORAGE_KEY));
  } catch {
    return null;
  }
};

const rememberReleaseAttempt = (releaseId) => {
  try {
    window.sessionStorage?.setItem(RELEASE_ATTEMPT_STORAGE_KEY, releaseId);
  } catch {}
};

const clearReleaseAttempt = () => {
  try {
    window.sessionStorage?.removeItem(RELEASE_ATTEMPT_STORAGE_KEY);
  } catch {}
};

const clearReleaseCacheBust = (releaseId) => {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('_ng_release') !== releaseId) return;
    url.searchParams.delete('_ng_release');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {}
};

const reloadForRelease = (releaseId) => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_ng_release', releaseId);
    window.location.replace(url.toString());
  } catch {
    window.location?.reload?.();
  }
};

const loadReleaseId = async ({
  fetchImpl = typeof window !== 'undefined' ? window.fetch : undefined,
} = {}) => {
  if (typeof fetchImpl !== 'function') return null;
  const response = await fetchImpl(`/release.json?t=${Date.now()}`, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
    },
  });
  if (!response?.ok) return null;
  const payload = await response.json();
  return toStringOrNull(payload?.release_id ?? payload?.releaseId ?? null);
};

export const checkForFrontendRelease = ({
  canReload = () => true,
  fetchImpl = typeof window !== 'undefined' ? window.fetch : undefined,
  reload = reloadForRelease,
} = {}) => {
  if (pendingReleaseId) {
    if (canReload() !== true) return Promise.resolve(false);
    if (readAttemptedReleaseId() === pendingReleaseId) {
      pendingReleaseId = null;
      return Promise.resolve(false);
    }
    rememberReleaseAttempt(pendingReleaseId);
    reload(pendingReleaseId);
    return Promise.resolve(true);
  }
  if (releaseCheckFlight) return releaseCheckFlight;

  releaseCheckFlight = (async () => {
    const currentReleaseId = toStringOrNull(getBuildVersion());
    const availableReleaseId =
      pendingReleaseId || (await loadReleaseId({ fetchImpl }).catch(() => null));
    if (!currentReleaseId || !availableReleaseId || availableReleaseId === currentReleaseId) {
      pendingReleaseId = null;
      if (currentReleaseId && availableReleaseId === currentReleaseId) {
        clearReleaseAttempt();
        clearReleaseCacheBust(currentReleaseId);
      }
      return false;
    }

    pendingReleaseId = availableReleaseId;
    if (canReload() !== true) return false;
    if (readAttemptedReleaseId() === availableReleaseId) {
      pendingReleaseId = null;
      return false;
    }
    rememberReleaseAttempt(availableReleaseId);
    reload(availableReleaseId);
    return true;
  })().finally(() => {
    releaseCheckFlight = null;
  });

  return releaseCheckFlight;
};

export const startFrontendReleaseMonitor = ({ canReload = () => true } = {}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  const check = () => {
    if (!document.hidden) {
      void checkForFrontendRelease({ canReload });
    }
  };
  const handleVisibility = () => {
    if (!document.hidden) check();
  };

  window.addEventListener('focus', check);
  window.addEventListener('online', check);
  document.addEventListener('visibilitychange', handleVisibility);
  const intervalId = window.setInterval(check, RELEASE_CHECK_INTERVAL_MS);
  check();

  return () => {
    window.removeEventListener('focus', check);
    window.removeEventListener('online', check);
    document.removeEventListener('visibilitychange', handleVisibility);
    window.clearInterval(intervalId);
  };
};

export const resetFrontendReleaseLifecycleForTests = () => {
  pendingReleaseId = null;
  releaseCheckFlight = null;
  clearReleaseAttempt();
};
